import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import Loading from 'src/components/Loading/intex';

import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import FileUpload, { CustomFileActions, FilesPropType } from 'src/components/FileUpload';
import Typography from 'src/components/Typography';
import downloadFile from 'src/utils/downloadFile';
import { addFile, deleteFile, importFileImportFetch, clearProductsImport } from 'src/resources/productsImport/reducer';
import { FileImportType } from 'src/types/productsImport';
import { FileType } from 'src/types/file';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { WarningIcon } from 'src/components/SkladonIcon/SmallIcons';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';
import Tooltip from 'src/components/Tooltip';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import ErrorRowsTable from './ErrorRowsTable';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ImportedTable from './ImportedTable';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import ColumnOptions from './ColumnOptions';
import productTypesWithoutVirtual from 'src/utils/productTypesWithoutVirtual';

const WarningButton = styled(IconButton)<IconButtonProps>({
    '& svg path': {
        fill: colors.primary_O,
    },
});

const findFile = (files: FileImportType[], inputFile: FileType) => {
    return files.find((file) => file.file.name === inputFile.name);
};

const ProductsImportContainer: FC = () => {
    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [showColumnOptions, setShowColumnOptions] = useState<boolean>(false);

    const loading = useSelector((state: RootStoreType) => state.productsImport.loading);
    const files = useSelector((state: RootStoreType) => state.productsImport.files);
    const importedData = useSelector((state: RootStoreType) => state.productsImport.importedData);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);

    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const expeditionMethodCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);

    const permissionCreate = useCheckPermission('PRODUCT.CREATE');
    const permissionUpdate = useCheckPermission('PRODUCT.UPDATE');
    const permissionEditDimensionsWeight = useCheckPermission('PRODUCT.EDIT_DIMENSIONS_WEIGHT');
    const disabled = !permissionCreate || !permissionUpdate;

    const disableEdit = disabled || !files.filter((file) => !file.imported).length;

    const backUrl = getBackUrl(locationHistory, '/products/active/');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('productsImport.title'), backUrl }));
    }, [backUrl, locationHistory]);

    useEffect(() => {
        return () => {
            dispatch(clearProductsImport());
        };
    }, []);

    let filesLoading = false;
    let filesError = false;

    const inputFiles = files.map((file) => {
        if (file.loading) {
            filesLoading = true;
        }
        if (file.validation?.errors.length) {
            filesError = true;
        }
        return file.file;
    });

    const customFileActions: CustomFileActions = (inputFile) => {
        const file = findFile(files, inputFile);
        if (!file) {
            return null;
        }
        return (
            <>
                {file.loading && (
                    <IconButton disabled>
                        <CircularProgress color="secondary" size={20} />
                    </IconButton>
                )}
                {!!file.validation?.errors.length && (
                    <Tooltip title={translate('productsImport.error')}>
                        <WarningButton>
                            <WarningIcon />
                        </WarningButton>
                    </Tooltip>
                )}
                {file.error && (
                    <Tooltip title={file.error}>
                        <WarningButton>
                            <WarningIcon />
                        </WarningButton>
                    </Tooltip>
                )}
                {file.validation?.errors.length === 0 && (
                    <IconButton disabled>
                        <TaskAltIcon style={{ fill: 'green' }} />
                    </IconButton>
                )}
            </>
        );
    };

    const dowlaodExcelPattern = () => {
        if (permissionEditDimensionsWeight) {
            downloadFile(`/xlsx/import_products_editall.xlsx`, 'importProducts.xlsx');
        } else {
            downloadFile(`/xlsx/import_products.xlsx`, 'importProducts.xlsx');
        }
    };

    const onUpdate = (files: FilesPropType) => {
        files.map((file) => loadFileData(file));
    };

    const onDelete = (file: FileType) => {
        dispatch(deleteFile(file));
    };

    const handleImport = () => {
        setConfirmParams({
            title: translate('productsImport.import'),
            content: translate('productsImport.importConfirm'),
            action: () => dispatch(importFileImportFetch()),
        });
    };

    // any becosue of reader.readAsBinaryString(file)
    const loadFileData = (file: any) => {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = () => {
            dispatch(addFile({ data: reader.result, name: file.name, size: file.size, type: file.type }));
        };
    };

    return (
        <>
            <TopBlock
                disabled={disabled}
                disableEdit={disableEdit}
                filesError={filesError}
                handleImport={handleImport}
            />
            <form ref={formRef}>
                {(loading || filesLoading) && <Loading />}
                <Typography component="div" gutterBottom>
                    <span onClick={dowlaodExcelPattern} style={{ cursor: 'pointer' }}>
                        {translate('productsImport.excelFilePattern')}
                        <IconButton color="primary">
                            <DownloadIcon />
                        </IconButton>
                    </span>
                    <Tooltip title={translate('productsImport.columnsOptions')}>
                        <IconButton onClick={() => setShowColumnOptions((value) => !value)}>
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <FileUpload
                    files={inputFiles}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    accept=".xls, .xlsx"
                    height={150}
                    multiple
                    disabled={disabled}
                    rawFiles
                    displayFilesList
                    disableShowFile
                    disableDowloadFile
                    customFileActions={customFileActions}
                />
                <ErrorRowsTable
                    productTypeCodes={productTypesWithoutVirtual(productTypeCodes)}
                    expeditionMethodCodes={expeditionMethodCodes}
                    files={files}
                    filesError={filesError}
                />
                <ImportedTable
                    productTypeCodes={productTypesWithoutVirtual(productTypeCodes)}
                    expeditionMethodCodes={expeditionMethodCodes}
                    importedData={importedData}
                />

                {showColumnOptions && (
                    <ColumnOptions
                        productTypeCodes={productTypesWithoutVirtual(productTypeCodes)}
                        expeditionMethodCodes={expeditionMethodCodes}
                        boolNumberCodes={boolNumberCodes}
                        scanImeiSnCodes={scanImeiSnCodes}
                        currencyCodes={currencyCodes}
                        onClose={() => setShowColumnOptions(false)}
                    />
                )}

                {confirmParams && (
                    <ConfirmDialog
                        title={confirmParams.title}
                        content={confirmParams.content}
                        action={confirmParams.action}
                        onClose={(): any => setConfirmParams(null)}
                    />
                )}
            </form>
        </>
    );
};

export default ProductsImportContainer;
